/**
 * FlexEngine — Workout Generator Engine
 *
 * Pure function: takes current app state → returns a fully specified workout plan.
 * No React, no Dexie — pure data transformation for testability.
 *
 * Algorithm sequence:
 *   1. Resolve split day → target muscle list (anterior | posterior)
 *   2. Filter exercise pool by available equipment
 *   3. Score each exercise by muscle need + fatigue availability
 *   4. Build set list: compounds first, then isolation, until time cap
 *   5. Inject warm-up sets for first 2 compound movements
 *   6. Compute suggested weights via progressive overload step function
 */

import { calcProgressiveWeight } from '@/hooks/use1RM';
import { computeCurrentFatigue } from '@/hooks/useFatigue';
import { SESSION_VOLUME_CAP } from '@/hooks/useWeeklyVolume';
import type {
  Exercise,
  MuscleGroup,
  MuscleState,
  GymProfile,
  AppSettings,
  GeneratedExercise,
  SuggestedSet,
  WarmupSet,
  LoggedSet,
  SplitDay,
  RPE,
  TrainingGoal,
  ExperienceLevel,
} from '@/types';

// ── Constants ────────────────────────────────────────────────

const EXECUTION_TIME_PER_SET = 30; // seconds
const FATIGUE_THRESHOLD = 0.5;

// ── Goal-aware parameters ─────────────────────────────────────

interface GoalParams {
  repRange: [number, number]; // [min, max]
  targetRPE: RPE;
  restMultiplier: number;     // multiplied against exercise.restSeconds
  setsPerExercise: number;
  minSessionSets: number;
  maxSessionSets: number;
  weeklyVolumeTarget: number;
}

const GOAL_PARAMS: Record<TrainingGoal, GoalParams> = {
  strength: {
    repRange: [3, 5],
    targetRPE: 9,
    restMultiplier: 1.5,   // longer rest for heavy sets
    setsPerExercise: 4,
    minSessionSets: 16,
    maxSessionSets: 20,
    weeklyVolumeTarget: 10,
  },
  hypertrophy: {
    repRange: [6, 12],
    targetRPE: 8,
    restMultiplier: 1.0,
    setsPerExercise: 3,
    minSessionSets: 20,
    maxSessionSets: 24,
    weeklyVolumeTarget: 15,
  },
  general_fitness: {
    repRange: [10, 15],
    targetRPE: 7,
    restMultiplier: 0.75,  // shorter rest, more circuit-style
    setsPerExercise: 3,
    minSessionSets: 18,
    maxSessionSets: 22,
    weeklyVolumeTarget: 12,
  },
};

/** Periodise rep targets across sets within a session (e.g. 8/8/6 descending) */
function periodisedReps(goal: TrainingGoal, setIndex: number): number {
  const [min, max] = GOAL_PARAMS[goal].repRange;
  // Set 1: high end; subsequent sets ramp slightly heavier/lower
  const step = Math.floor((max - min) / 3);
  return Math.max(min, max - setIndex * step);
}

/** Experience-based volume multiplier: beginners do less total volume */
function experienceVolumeFactor(level: ExperienceLevel): number {
  return level === 'beginner' ? 0.75 : level === 'intermediate' ? 1.0 : 1.2;
}

// Muscle priority lists per split day
// Ordering matters: higher-priority muscles get first compound slot

/**
 * Push day WITHOUT quads — used when a dedicated leg day exists (PPL, Upper/Lower).
 * Quads should not appear on push day if they get their own session.
 */
const PUSH_MUSCLES_NO_LEGS: MuscleGroup[] = [
  'chest', 'front_delt', 'lateral_delt', 'triceps', 'core',
];
/**
 * Push day WITH quads — used when there is no dedicated leg day (Push/Pull only).
 */
const PUSH_MUSCLES: MuscleGroup[] = [
  'chest', 'front_delt', 'lateral_delt', 'triceps', 'quads', 'core',
];
const PULL_MUSCLES_NO_LEGS: MuscleGroup[] = [
  'lats', 'rhomboids', 'rear_delt', 'biceps', 'traps', 'core',
];
const PULL_MUSCLES: MuscleGroup[] = [
  'lats', 'rhomboids', 'rear_delt', 'biceps', 'hamstrings', 'glutes', 'calves',
];
const LEGS_MUSCLES: MuscleGroup[] = [
  'quads', 'hamstrings', 'glutes', 'calves', 'hip_flexors', 'lower_back',
];
const UPPER_MUSCLES: MuscleGroup[] = [
  'chest', 'lats', 'front_delt', 'lateral_delt', 'rear_delt',
  'triceps', 'biceps', 'rhomboids', 'traps',
];
const LOWER_MUSCLES: MuscleGroup[] = [
  'quads', 'hamstrings', 'glutes', 'calves', 'hip_flexors', 'lower_back', 'core',
];
const FULL_BODY_MUSCLES: MuscleGroup[] = [
  'chest', 'lats', 'quads', 'hamstrings', 'glutes',
  'front_delt', 'rear_delt', 'triceps', 'biceps', 'core',
];

// Legacy aliases kept for any old data that still references them
const ANTERIOR_MUSCLES = PUSH_MUSCLES;
const POSTERIOR_MUSCLES = PULL_MUSCLES;

const SPLIT_DAY_MUSCLES: Record<import('@/types').SplitDay, MuscleGroup[]> = {
  push:          PUSH_MUSCLES,       // overridden by splitType check below
  anterior:      PUSH_MUSCLES,       // legacy
  pull:          PULL_MUSCLES,       // overridden by splitType check below
  posterior:     PULL_MUSCLES,       // legacy
  legs:          LEGS_MUSCLES,
  upper:         UPPER_MUSCLES,
  lower:         LOWER_MUSCLES,
  full_body_day: FULL_BODY_MUSCLES,
};

/** Returns the correct muscle target list given split day AND split type. */
function resolveTargetMuscles(
  splitDay: import('@/types').SplitDay,
  splitType: import('@/types').SplitType,
): MuscleGroup[] {
  const hasDedicatedLegDay = splitType === 'push_pull_legs' || splitType === 'upper_lower';
  if ((splitDay === 'push' || splitDay === 'anterior') && hasDedicatedLegDay) {
    return PUSH_MUSCLES_NO_LEGS;
  }
  if ((splitDay === 'pull' || splitDay === 'posterior') && hasDedicatedLegDay) {
    return PULL_MUSCLES_NO_LEGS;
  }
  return SPLIT_DAY_MUSCLES[splitDay] ?? PUSH_MUSCLES;
}

void ANTERIOR_MUSCLES; void POSTERIOR_MUSCLES; // suppress unused warnings

// ── Types ────────────────────────────────────────────────────

export interface GeneratorInput {
  splitDay: SplitDay;
  availableExercises: Exercise[];
  muscleStates: Map<MuscleGroup, MuscleState>;
  /** exerciseId → most recent working sets (for progressive overload) */
  exerciseHistory: Map<string, LoggedSet[]>;
  /** exerciseId → usage count (for substitution sorting) */
  exerciseUsageCount: Map<string, number>;
  gymProfile: GymProfile;
  settings: AppSettings;
  /** Muscles already in session volume cap */
  sessionVolume: Map<MuscleGroup, number>;
}

export interface GeneratorOutput {
  splitDay: SplitDay;
  exercises: GeneratedExercise[];
  estimatedDurationSeconds: number;
  totalWorkingSets: number;
  warnings: string[];
}

// ── Utility ──────────────────────────────────────────────────

function estimateSetDuration(exercise: Exercise): number {
  return EXECUTION_TIME_PER_SET + exercise.restSeconds;
}

/**
 * Build warm-up sets for a compound movement.
 * 2 sets always; 3rd set added if working weight > 150 lbs.
 */
function buildWarmupSets(targetWeightLbs: number): WarmupSet[] {
  const warmups: WarmupSet[] = [
    {
      setNumber: 1,
      targetWeightLbs: Math.round((targetWeightLbs * 0.5) / 2.5) * 2.5,
      targetReps: 10,
      restSeconds: 45,
    },
    {
      setNumber: 2,
      targetWeightLbs: Math.round((targetWeightLbs * 0.7) / 2.5) * 2.5,
      targetReps: 5,
      restSeconds: 65,
    },
  ];

  if (targetWeightLbs > 150) {
    warmups.push({
      setNumber: 3,
      targetWeightLbs: Math.round((targetWeightLbs * 0.9) / 2.5) * 2.5,
      targetReps: 2,
      restSeconds: 90,
    });
  }

  return warmups;
}

/**
 * Build working set suggestions using progressive overload + goal-aware rep ranges.
 */
function buildWorkingSets(
  exercise: Exercise,
  history: LoggedSet[],
  count: number,
  goal: TrainingGoal,
  isDeload = false,
): SuggestedSet[] {
  const roundTo: 2.5 | 5 =
    exercise.equipment.includes('barbell') || exercise.equipment.includes('trap_bar') ? 5 : 2.5;

  const lastSet = [...history]
    .filter((s) => !s.isWarmup)
    .sort((a, b) => b.loggedAt.localeCompare(a.loggedAt))[0];

  let suggestedWeight: number;
  if (lastSet) {
    const base = calcProgressiveWeight(lastSet.weightLbs, lastSet.rpe as RPE, roundTo);
    suggestedWeight = isDeload ? Math.round((base * 0.5) / roundTo) * roundTo : base;
  } else {
    const defaults: Record<string, number> = {
      horizontal_push: 95, vertical_push: 65, horizontal_pull: 95,
      vertical_pull: 0, hip_hinge: 135, squat: 95, lunge: 30,
      isolation_push: 25, isolation_pull: 20,
      core_anti_extension: 0, core_rotation: 15, carry: 35,
    };
    suggestedWeight = defaults[exercise.pattern] ?? 45;
  }

  const gp = GOAL_PARAMS[goal];
  const restSeconds = Math.round(exercise.restSeconds * gp.restMultiplier);

  return Array.from({ length: count }, (_, i) => ({
    setNumber: i + 1,
    suggestedWeightLbs: suggestedWeight,
    targetReps: periodisedReps(goal, i),
    targetRPE: gp.targetRPE,
    restSeconds,
  }));
}

/**
 * Score an exercise for inclusion in today's session.
 * Higher score = higher priority.
 */
function scoreExercise(
  exercise: Exercise,
  targetMuscles: MuscleGroup[],
  muscleStates: Map<MuscleGroup, MuscleState>,
  weeklyVolume: Map<MuscleGroup, number>,
  sessionVolume: Map<MuscleGroup, number>,
  weeklyTarget: number,
): number {
  let score = 0;

  const primaryMuscle = exercise.muscles[0]?.muscle;
  if (!primaryMuscle) return -Infinity;

  // Prefer primary muscles that are in the split
  const splitIndex = targetMuscles.indexOf(primaryMuscle);
  if (splitIndex === -1) return -Infinity;

  // Higher priority to muscles earlier in the split list
  score += (targetMuscles.length - splitIndex) * 10;

  // Prefer muscles with higher volume deficit (more need)
  const currentVol = weeklyVolume.get(primaryMuscle) ?? 0;
  const deficit = Math.max(0, weeklyTarget - currentVol);
  score += deficit * 3;

  // Penalise if session volume cap is already hit for this muscle
  const sessionVol = sessionVolume.get(primaryMuscle) ?? 0;
  if (sessionVol >= SESSION_VOLUME_CAP) score -= 1000;

  // Prefer fresher muscles
  const state = muscleStates.get(primaryMuscle);
  if (state) {
    const fatigue = computeCurrentFatigue(state);
    score += (1 - fatigue) * 5;
  }

  // Compound exercises score higher than isolation
  if (exercise.category === 'compound') score += 8;

  return score;
}

// ── Main Generator ────────────────────────────────────────────

export function generateWorkout(input: GeneratorInput): GeneratorOutput {
  const {
    splitDay,
    availableExercises,
    muscleStates,
    exerciseHistory,
    gymProfile,
    settings,
    sessionVolume,
  } = input;

  const warnings: string[] = [];
  const splitType = settings.splitType ?? 'push_pull';
  const targetMuscles = resolveTargetMuscles(splitDay, splitType);

  // Resolve goal-aware parameters
  const goal       = settings.trainingGoal      ?? 'hypertrophy';
  const level      = settings.experienceLevel   ?? 'intermediate';
  const isDeload   = settings.deloadRecommended ?? false;
  const gp         = GOAL_PARAMS[goal];
  const volFactor  = experienceVolumeFactor(level) * (isDeload ? 0.5 : 1);
  const weeklyTarget = settings.weeklyVolumeTarget ?? gp.weeklyVolumeTarget;

  if (isDeload) {
    warnings.push('Deload week — volume and intensity reduced 50% for recovery');
  }

  // Session timing: respect user's preferred duration
  const preferredMinutes = settings.preferredDurationMinutes ?? 60;
  const preferredSeconds = preferredMinutes * 60;

  // Scale sets-per-exercise down for shorter sessions so there's room for variety
  const durationScale =
    preferredMinutes <= 30 ? 0.67 :
    preferredMinutes <= 45 ? 0.85 :
    preferredMinutes >= 75 ? 1.15 : 1.0;
  const setsPerExercise = Math.max(2, Math.round(gp.setsPerExercise * durationScale));

  // Min sets scales with duration — short session should still feel complete, not sparse
  const MIN_SESSION_SETS = Math.round(gp.minSessionSets * durationScale);
  const MAX_SESSION_SETS = Math.round(gp.maxSessionSets * volFactor * durationScale);

  // ── 1. Filter by equipment ───────────────────────────────────
  const equipmentPool = availableExercises.filter((ex) =>
    ex.equipment.some((eq) => gymProfile.availableEquipment.includes(eq)),
  );

  // ── 2. Filter out exercises where primary muscle is fatigued or injured ─
  const viablePool = equipmentPool.filter((ex) => {
    const primaryMuscle = ex.muscles[0]?.muscle;
    if (!primaryMuscle) return false;

    // Must be in today's split
    if (!targetMuscles.includes(primaryMuscle)) return false;

    const state = muscleStates.get(primaryMuscle);
    if (!state) return true; // no data → treat as fresh

    if (state.isInjured) return false;
    if (computeCurrentFatigue(state) > FATIGUE_THRESHOLD) {
      warnings.push(`${primaryMuscle} is too fatigued — skipping exercises that target it`);
      return false;
    }

    return true;
  });

  if (viablePool.length === 0) {
    warnings.push('No exercises available — all targeted muscles are fatigued or injured');
    return {
      splitDay,
      exercises: [],
      estimatedDurationSeconds: 0,
      totalWorkingSets: 0,
      warnings,
    };
  }

  // ── 4. Score & sort pool ────────────────────────────────────
  const scored = viablePool
    .map((ex) => ({
      exercise: ex,
      score: scoreExercise(
        ex,
        targetMuscles,
        muscleStates,
        input.weeklyVolume ?? new Map(),
        sessionVolume,
        weeklyTarget,
      ),
    }))
    .filter((e) => e.score > -500)
    .sort((a, b) => {
      // Compounds before isolation within same score tier
      if (b.score !== a.score) return b.score - a.score;
      if (a.exercise.category !== b.exercise.category) {
        return a.exercise.category === 'compound' ? -1 : 1;
      }
      return 0;
    });

  // ── 5. Greedy selection with time-bounding ──────────────────
  const selected: { exercise: Exercise; workingSetCount: number }[] = [];
  let totalSets = 0;
  let estimatedSeconds = 0;
  const localSessionVolume = new Map(sessionVolume);

  // Deduplicate by primary muscle — one compound + one isolation max per muscle
  const compoundPerMuscle = new Set<MuscleGroup>();
  const isolationPerMuscle = new Set<MuscleGroup>();

  for (const { exercise } of scored) {
    if (totalSets >= MAX_SESSION_SETS) break;
    if (estimatedSeconds >= preferredSeconds) break;

    const primaryMuscle = exercise.muscles[0]?.muscle as MuscleGroup;
    const sessionVol = localSessionVolume.get(primaryMuscle) ?? 0;
    if (sessionVol >= SESSION_VOLUME_CAP) continue;

    // At most 1 compound + 1 isolation per primary muscle group
    if (exercise.category === 'compound') {
      if (compoundPerMuscle.has(primaryMuscle)) continue;
      compoundPerMuscle.add(primaryMuscle);
    } else {
      if (isolationPerMuscle.has(primaryMuscle)) continue;
      isolationPerMuscle.add(primaryMuscle);
    }

    // How many working sets can we fit?
    const setsAvailable = Math.min(
      setsPerExercise,
      MAX_SESSION_SETS - totalSets,
      SESSION_VOLUME_CAP - sessionVol,
    );
    if (setsAvailable <= 0) continue;

    const timeForSets = setsAvailable * estimateSetDuration(exercise);
    if (estimatedSeconds + timeForSets > preferredSeconds && totalSets >= MIN_SESSION_SETS) break;

    selected.push({ exercise, workingSetCount: setsAvailable });
    totalSets += setsAvailable;
    estimatedSeconds += timeForSets;

    // Update local session volume tracker for synergists
    for (const alloc of exercise.muscles) {
      const cur = localSessionVolume.get(alloc.muscle) ?? 0;
      localSessionVolume.set(alloc.muscle, cur + alloc.fraction * setsAvailable);
    }
  }

  if (totalSets < MIN_SESSION_SETS) {
    warnings.push(`Session has only ${totalSets} sets — consider adjusting fatigue thresholds`);
  }

  // ── 6. Build GeneratedExercise objects ──────────────────────
  let warmupEligibleCount = 0;

  const generatedExercises: GeneratedExercise[] = selected.map(
    ({ exercise, workingSetCount }) => {
      const history = exerciseHistory.get(exercise.id) ?? [];
      const workingSets = buildWorkingSets(exercise, history, workingSetCount, goal, isDeload);
      const suggestedWeight = workingSets[0]?.suggestedWeightLbs ?? 0;

      // Inject warm-ups for first 2 compound movements only
      let warmupSets: WarmupSet[] = [];
      if (
        exercise.warmupEligible &&
        exercise.category === 'compound' &&
        warmupEligibleCount < 2 &&
        suggestedWeight > 0
      ) {
        warmupSets = buildWarmupSets(suggestedWeight);
        warmupEligibleCount++;
        // Add warm-up time (excluded from set cap but tracked for duration)
        estimatedSeconds += warmupSets.reduce(
          (t, s) => t + EXECUTION_TIME_PER_SET + s.restSeconds,
          0,
        );
      }

      return { exercise, warmupSets, workingSets };
    },
  );

  return {
    splitDay,
    exercises: generatedExercises,
    estimatedDurationSeconds: Math.round(estimatedSeconds),
    totalWorkingSets: totalSets,
    warnings,
  };
}

// Extend GeneratorInput to carry weeklyVolume (used in scoring)
declare module './WorkoutGenerator' {
  interface GeneratorInput {
    weeklyVolume?: Map<MuscleGroup, number>;
  }
}
